import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  signIn, confirmSignIn, signOut, signUp, confirmSignUp, resendSignUpCode,
  resetPassword, confirmResetPassword, updatePassword,
  getCurrentUser, fetchAuthSession, fetchUserAttributes,
} from 'aws-amplify/auth';
import { API_CONFIG } from '../config';

const AuthContext = createContext(null);

// ---------------------------------------------------------------------------
// Partner-API token cache — module-local, in-memory, per-env
//
// The Dev Portal authenticates to the developer API with a Cognito JWT, but
// to call the partner API (/v1/*) it needs an st_* bearer. We mint one via
// GET /developer/playground-token?tenant=<env>&ttl=900 (15-min TTL for live-
// tenant blast-radius) and cache per-env in memory ONLY — security audit
// guidance forbids persisting partner-API tokens to localStorage.
//
// Two-key cache (live + test) lets the browser hold both concurrently —
// useful if a page is rendering one env while a background refetch happens
// against the other.
//
// Refresh strategy: re-mint when the cached token is within REFRESH_BEFORE_MS
// of expiry. With ttl=900 and REFRESH_BEFORE_MS=60s, that's ~one mint per
// 14 minutes per env per browser session. Cheap.
//
// Concurrent-mint coalescing (added MR #6.5): multiple concurrent callers
// during the refresh window share a single in-flight Promise instead of
// each starting their own mint. The previous version did N mints for N
// callers — wasteful at best, mint-quota-exhausting at worst.
// `partnerTokenInFlight` tracks the live + test in-flight mints separately
// so a 'live' caller doesn't block a 'test' caller. The in-flight slot is
// cleared in a finally block so a failed mint can be retried on the next
// call. Pinned by AuthContext.test.js "two concurrent callers coalesce".
//
// Clear-during-mint defense (MR #6.5): a monotonically-incrementing
// `cacheGeneration` counter is captured when a mint starts and re-checked
// before the mint's result is written to the cache. If a logout (or any
// other path through `clearPartnerApiTokenCache`) bumps the generation
// while the mint is in flight, the result is THROWN AWAY rather than
// landing in the cache where the next-logged-in identity could read it.
// Without this guard, the contract "logout clears the partner-API tokens"
// was a lie for the 100-2000ms window between mint start and mint finish.
// Pinned by AuthContext.test.js "cache cleared during in-flight mint".
// ---------------------------------------------------------------------------

const partnerTokenCache = { live: null, test: null };
const partnerTokenExpiry = { live: 0, test: 0 };
const partnerTokenInFlight = { live: null, test: null };
let cacheGeneration = 0;
const REFRESH_BEFORE_MS = 60_000;
const PARTNER_TOKEN_TTL_SECONDS = 900;

/**
 * Get a partner-API st_* bearer token for the given env, minting a fresh one
 * if the cached value is absent or near expiry. Returns the token string —
 * callers (the partnerApi.js axios interceptor) attach it as
 * `Authorization: Bearer <token>`.
 *
 * Exported as a plain function (not a hook) so non-React code paths (the
 * axios interceptor) can call it without React context plumbing.
 *
 * Concurrent callers for the SAME env share one in-flight mint Promise.
 * See the module-level comment above for the rationale.
 *
 * @param {'live'|'test'} env which Vectros tenant to scope the token to
 * @returns {Promise<string>} the raw st_* token
 * @throws if the developer isn't authenticated or the mint endpoint fails
 */
export function getPartnerApiToken(env = 'test') {
  if (env !== 'live' && env !== 'test') {
    return Promise.reject(
      new Error(`getPartnerApiToken: env must be 'live' or 'test', got '${env}'`),
    );
  }

  const now = Date.now();
  if (partnerTokenCache[env] && partnerTokenExpiry[env] > now + REFRESH_BEFORE_MS) {
    return Promise.resolve(partnerTokenCache[env]);
  }

  // If a mint for this env is already running, join it instead of starting
  // a second one. The synchronous assignment below (before the IIFE yields
  // at its first await) ensures that a second caller arriving in the same
  // tick sees the slot populated.
  if (partnerTokenInFlight[env]) {
    return partnerTokenInFlight[env];
  }

  // Capture the cache generation BEFORE the mint starts. If a clear bumps
  // the generation while the mint is in flight, we discard the result —
  // see the module-level comment for the threat model.
  const generationAtStart = cacheGeneration;

  const mintPromise = (async () => {
    try {
      const { tokens } = await fetchAuthSession();
      const idToken = tokens?.idToken?.toString();
      if (!idToken) throw new Error('Not authenticated — cannot mint partner-API token');

      // context=vectros-admin: the Dev Portal's partner-API calls (Keys, API
      // Logs) are CONTROL-PLANE admin operations, which live in the reserved
      // vectros-admin context. The playground-token mint defaults to the
      // 'default' data context when context is absent (WP-3), so we must request
      // vectros-admin explicitly — for an OWNER it's validated as existing
      // in-tenant (auto-seeded), and for a member it resolves their vectros-admin
      // AccessProfile (else uniform 404).
      const url = `${API_CONFIG.developerApiBase}/developer/playground-token`
                + `?tenant=${encodeURIComponent(env)}&ttl=${PARTNER_TOKEN_TTL_SECONDS}&context=vectros-admin`;
      const resp = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (!resp.ok) {
        const body = await resp.text().catch(() => '');
        throw new Error(`Partner-API token mint failed: ${resp.status} ${body}`);
      }
      const { token, expiresAt } = await resp.json();

      // If clearPartnerApiTokenCache fired while this mint was in flight,
      // the user this token was minted for no longer has an active session.
      // Throw away the result rather than writing it to the cache where the
      // next-logged-in user could read it. The CALLER receives a rejection;
      // for the original (now-logged-out) caller that's fine — they're no
      // longer waiting on a response.
      if (cacheGeneration !== generationAtStart) {
        throw new Error('Session cleared during partner-API token mint; discarding result.');
      }
      partnerTokenCache[env] = token;
      partnerTokenExpiry[env] = expiresAt * 1000; // backend returns epoch seconds
      return token;
    } finally {
      // Release the in-flight slot — but only if it still references THIS
      // mint. clearPartnerApiTokenCache nulls the slot directly, and a
      // subsequent caller (now in a fresh session) may have already started
      // a new mint and re-populated the slot. Don't clobber theirs.
      if (partnerTokenInFlight[env] === mintPromise) {
        partnerTokenInFlight[env] = null;
      }
    }
  })();
  partnerTokenInFlight[env] = mintPromise;
  return mintPromise;
}

/**
 * Clear cached partner-API tokens, any in-flight mints, AND advance the
 * cache generation so any mint that started before this call discards its
 * result instead of writing it to the cache.
 *
 * Without the generation bump, the contract "logout clears the partner-API
 * tokens" was a lie for the time window between mint start and mint finish:
 *   1. User A's getPartnerApiToken in flight.
 *   2. User A logs out → clearPartnerApiTokenCache nulls everything.
 *   3. Mint resolves; writes User A's token to the cache.
 *   4. User B logs in; first getPartnerApiToken call reads User A's token
 *      from the cache and uses it as User B's bearer.
 * That's a real-world cross-identity token leak. The generation counter
 * guards step 3 — the mint's try block re-checks generation before writing
 * to the cache and throws if it changed.
 */
export function clearPartnerApiTokenCache() {
  cacheGeneration += 1;
  partnerTokenCache.live = null;
  partnerTokenCache.test = null;
  partnerTokenExpiry.live = 0;
  partnerTokenExpiry.test = 0;
  partnerTokenInFlight.live = null;
  partnerTokenInFlight.test = null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       // { email, sub, liveTenantId, testTenantId, partnerId }
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    try {
      const attrs = await fetchUserAttributes();
      setUser({
        email: attrs.email,
        sub: attrs.sub,
        uuid: attrs['custom:uuid'] || null,
        liveTenantId: attrs['custom:live_tenant_id'] || null,
        testTenantId: attrs['custom:test_tenant_id'] || null,
        partnerId: attrs['custom:partner_id'] || null,
        firstName: attrs.given_name || '',
        lastName: attrs.family_name || '',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getCurrentUser()
      .then(() => loadUser())
      .catch(() => setLoading(false));
  }, [loadUser]);

  const login = async (email, password) => {
    const { nextStep } = await signIn({ username: email.toLowerCase(), password });
    if (nextStep.signInStep === 'DONE') {
      await loadUser();
    }
    // Return step so callers can handle MFA or other challenges
    return nextStep.signInStep;
  };

  // Complete an MFA code challenge (TOTP / SMS / EMAIL). Cognito MFA preference
  // is a property of the shared DeveloperUserPool user, so a developer who
  // enrolls TOTP in the Vectros Admin app is challenged HERE too — this portal
  // must be able to answer it (admin-app MR #7.97 §5). Returns the next step;
  // loads the user on DONE.
  const confirmMfa = async (code) => {
    const { nextStep } = await confirmSignIn({ challengeResponse: code });
    if (nextStep.signInStep === 'DONE') {
      await loadUser();
    }
    return nextStep.signInStep;
  };

  const register = async ({ email, password, firstName, lastName, captchaToken }) => {
    const uuid        = crypto.randomUUID();
    const partnerId   = crypto.randomUUID();
    const liveTenantId = crypto.randomUUID();
    const testTenantId = crypto.randomUUID();

    await signUp({
      username: email.toLowerCase(),
      password,
      options: {
        userAttributes: {
          email: email.toLowerCase(),
          given_name: firstName,
          family_name: lastName,
          'custom:type':           'developer',
          'custom:uuid':           uuid,
          'custom:partner_id':     partnerId,
          'custom:live_tenant_id': liveTenantId,
          'custom:test_tenant_id': testTenantId,
        },
        // Anti-bot challenge token (Cloudflare Turnstile — ADR-0021). Cognito
        // forwards clientMetadata to the PreSignup trigger as
        // event.request.clientMetadata, where it is verified server-side. Only
        // sent when a token was produced (widget configured + solved); the
        // backend CAPTCHA_MODE gate decides whether a missing token is allowed.
        ...(captchaToken ? { clientMetadata: { captchaToken } } : {}),
      },
    });
  };

  const confirmEmail = async (email, code) => {
    await confirmSignUp({ username: email.toLowerCase(), confirmationCode: code });
  };

  const resendCode = async (email) => {
    await resendSignUpCode({ username: email.toLowerCase() });
  };

  const forgotPassword = async (email) => {
    await resetPassword({ username: email.toLowerCase() });
  };

  const confirmForgotPassword = async (email, code, newPassword) => {
    await confirmResetPassword({
      username: email.toLowerCase(),
      confirmationCode: code,
      newPassword,
    });
  };

  const changePassword = async (oldPassword, newPassword) => {
    await updatePassword({ oldPassword, newPassword });
  };

  const logout = async () => {
    await signOut({ global: true });
    clearPartnerApiTokenCache();
    setUser(null);
  };

  const getIdToken = async () => {
    const { tokens } = await fetchAuthSession();
    return tokens?.idToken?.toString() || null;
  };

  return (
    <AuthContext.Provider value={{
      user, loading,
      login, confirmMfa, register, confirmEmail, resendCode,
      forgotPassword, confirmForgotPassword, changePassword,
      logout, getIdToken,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
